/** Safe target size for Firestore documents (1 MB hard limit). */
export const FIRESTORE_TARGET_DOC_BYTES = 750_000;

/** Firestore field overhead fudge factor per map entry (bytes). */
const ENTRY_OVERHEAD_BYTES = 32;

/**
 * Estimate serialized size of one map entry in bytes (key + JSON value + overhead).
 */
export function estimateEntryBytes(encodedKey: string, value: unknown): number {
  const keyBytes = encodedKey.length * 2;
  let valueBytes = 0;
  try {
    valueBytes = new TextEncoder().encode(JSON.stringify(value)).length;
  } catch {
    valueBytes = 64;
  }
  return keyBytes + valueBytes + ENTRY_OVERHEAD_BYTES;
}

export interface IChunkWritePlan<T> {
  chunkId: string;
  entries: Record<string, T>;
}

/**
 * Distribute entries across chunk documents, each staying under targetBytes.
 */
export function planChunkWrites<T>(
  entryList: { key: string; value: T }[],
  targetBytes: number = FIRESTORE_TARGET_DOC_BYTES,
  chunkIdPrefix: string = 'chunk',
): IChunkWritePlan<T>[] {
  const plans: IChunkWritePlan<T>[] = [];
  let currentEntries: Record<string, T> = {};
  let currentSize = 0;
  let chunkIndex = 0;

  const flush = (): void => {
    if (Object.keys(currentEntries).length === 0) {
      return;
    }
    plans.push({
      chunkId: `${chunkIdPrefix}_${String(chunkIndex).padStart(3, '0')}`,
      entries: { ...currentEntries },
    });
    chunkIndex += 1;
    currentEntries = {};
    currentSize = 0;
  };

  for (const { key, value } of entryList) {
    const entrySize = estimateEntryBytes(key, value);
    if (entrySize > targetBytes) {
      flush();
      plans.push({ chunkId: `${chunkIdPrefix}_${String(chunkIndex).padStart(3, '0')}`, entries: { [key]: value } });
      chunkIndex += 1;
      continue;
    }
    if (currentSize + entrySize > targetBytes && Object.keys(currentEntries).length > 0) {
      flush();
    }
    currentEntries[key] = value;
    currentSize += entrySize;
  }
  flush();
  return plans;
}

/**
 * Merge new entries into an existing chunk map, splitting overflow into additional chunks.
 */
export function mergeEntriesIntoChunks<T>(
  existingChunks: { chunkId: string; entries: Record<string, T> }[],
  newEntries: { key: string; value: T }[],
  targetBytes: number = FIRESTORE_TARGET_DOC_BYTES,
  chunkIdPrefix: string = 'chunk',
): { chunkId: string; entries: Record<string, T> }[] {
  const merged = new Map<string, Record<string, T>>();
  for (const chunk of existingChunks) {
    merged.set(chunk.chunkId, { ...chunk.entries });
  }
  let tailChunkId = existingChunks.length > 0
    ? existingChunks[existingChunks.length - 1].chunkId
    : `${chunkIdPrefix}_000`;
  if (!merged.has(tailChunkId)) {
    merged.set(tailChunkId, {});
  }

  for (const { key, value } of newEntries) {
    let placed = false;
    for (const [chunkId, entries] of merged.entries()) {
      const trial = { ...entries, [key]: value };
      const size = Object.entries(trial).reduce(
        (sum, [k, v]) => sum + estimateEntryBytes(k, v),
        0,
      );
      if (size <= targetBytes) {
        merged.set(chunkId, trial);
        placed = true;
        break;
      }
    }
    if (!placed) {
      const nextIndex = merged.size;
      const newChunkId = `${chunkIdPrefix}_${String(nextIndex).padStart(3, '0')}`;
      merged.set(newChunkId, { [key]: value });
      tailChunkId = newChunkId;
    }
  }

  return Array.from(merged.entries()).map(([chunkId, entries]) => ({ chunkId, entries }));
}
